// Vendor Types
export const VENDOR_TYPES = {
    CONSULTANT: 'Consultant',
    SUBCONTRACTOR: 'Subcontractor',
    MATERIAL_SUPPLIER: 'Material Supplier',
    SUB_AND_SUPPLIER: 'Sub and Supplier',
} as const;

export type VendorType = (typeof VENDOR_TYPES)[keyof typeof VENDOR_TYPES];

// Federal Certification Types with their IDs and form field names
export const FEDERAL_CERTIFICATIONS = {
    SBE: {
        id: 1,
        name: 'Small Business Enterprise (SBE)',
        formField: 'sBEForm' as const,
    },
    MBE: {
        id: 2,
        name: 'Minority Owned Business Enterprise (MBE)',
        formField: 'mBEForm' as const,
    },
    WBE: {
        id: 3,
        name: 'Woman Owned Business Enterprise (WBE)',
        formField: 'wBEForm' as const,
    },
    VBE: {
        id: 4,
        name: 'Veteran Owned Business Enterprise (VBE)',
        formField: 'vBEForm' as const,
    },
    USBLN: {
        id: 5,
        name: 'People with Disabilities (USBLN)',
        formField: 'uSBLNForm' as const,
    },
    HZBE: {
        id: 6,
        name: 'HUB Zone Business Enterprise (HZBE)',
        formField: 'hZBEForm' as const,
    },
    DBE: {
        id: 7,
        name: 'Small Disadvantaged Business Enterprise (DBE)',
        formField: 'dBEForm' as const,
    },
    DVBE: {
        id: 8,
        name: 'Service-Disabled Veteran Owned Business Enterprise (DVBE)',
        formField: 'dVBEForm' as const,
    },
    LGBTBE: {
        id: 9,
        name: 'Lesbian, Gay, Bisexual and/or Transgender Owned Business Enterprise (LGBTBE)',
        formField: 'lGBTBEForm' as const,
    },
} as const;

export type FederalCertificationKey = keyof typeof FEDERAL_CERTIFICATIONS;

// Referral Sources
export const REFERRAL_SOURCES = [
    'LinkedIn',
    'Estimating request',
    'Word of mouth/ referral',
    'Search engine',
    'Social Media',
    'Other',
] as const;

// Labor Status Options
export const LABOR_STATUS_OPTIONS = [
    'Open Shop',
    'Merit',
    'Non-Union',
    'Union',
] as const;

// Default country for US-based forms
export const DEFAULT_COUNTRY = 'United States of America';

// Helper function: Does this vendor type require trades/licenses/service areas?
export function requiresTradesAndLicenses(vendorType: string): boolean {
    return vendorType !== VENDOR_TYPES.MATERIAL_SUPPLIER && vendorType !== '';
}

// Helper function: Does this vendor type require subcontractor acknowledgment?
export function requiresSubAcknowledgment(vendorType: string): boolean {
    return (
        vendorType === VENDOR_TYPES.SUBCONTRACTOR ||
        vendorType === VENDOR_TYPES.SUB_AND_SUPPLIER
    );
}

// Helper function: Get federal certification by name
export function getCertificationByName(name: string) {
    return Object.values(FEDERAL_CERTIFICATIONS).find((c) => c.name === name);
}

// Helper function: Get federal certification key by name
export function getCertificationKeyByName(
    name: string
): FederalCertificationKey | undefined {
    const entry = Object.entries(FEDERAL_CERTIFICATIONS).find(
        ([, c]) => c.name === name
    );
    return entry ? (entry[0] as FederalCertificationKey) : undefined;
}
